import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Radio, Lock, Users, DollarSign, Plus, Zap, Eye, Shield, Crown, Video } from 'lucide-react';
import { motion } from 'framer-motion';
import { creatorAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';

const CATEGORIES = ['general', 'education', 'fitness', 'music', 'cooking', 'tech', 'art', 'gaming', 'lifestyle', 'adult'];

export default function CreatorHubPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [myRooms, setMyRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdult, setShowAdult] = useState(false);
  const [ageVerified, setAgeVerified] = useState(false);
  const [newRoom, setNewRoom] = useState({ name: '', description: '', category: 'general', price_per_month: 10, is_adult: false, is_broadcast: true, preview_text: '' });

  useEffect(() => {
    const fetch = async () => {
      try {
        const [pRes, mRes] = await Promise.all([
          creatorAPI.listRooms({ adult: showAdult }),
          creatorAPI.myRooms().catch(() => ({ data: [] })),
        ]);
        setRooms(pRes.data);
        setMyRooms(mRes.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, [showAdult]);

  const handleCreate = async () => {
    if (!newRoom.name.trim()) { toast.error('Name required'); return; }
    try {
      const res = await creatorAPI.createRoom(newRoom);
      toast.success('Room created!');
      setMyRooms([res.data, ...myRooms]);
      setNewRoom({ name: '', description: '', category: 'general', price_per_month: 10, is_adult: false, is_broadcast: true, preview_text: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleAdultToggle = () => {
    if (!ageVerified && !showAdult) {
      if (window.confirm('You must be 18+ to view adult content. By proceeding, you confirm you are of legal age and consent to viewing adult content.')) {
        setAgeVerified(true);
        setShowAdult(true);
      }
    } else {
      setShowAdult(!showAdult);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#b8ff00] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-zinc-50 pb-8">
      <section className="bg-white border-b border-zinc-200">
        <div className="max-w-5xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-rose-500 to-pink-600 rounded-2xl flex items-center justify-center">
                <Radio className="text-white" size={28} />
              </div>
              <div>
                <h1 className="font-display font-bold text-2xl text-zinc-900" data-testid="creator-hub-title">Creator Hub</h1>
                <p className="text-zinc-500 text-sm">Live rooms, encrypted broadcasts, premium content</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 px-3 py-1.5 bg-green-50 rounded-full border border-green-200">
                <Shield size={12} className="text-green-600" />
                <span className="text-[10px] text-green-700 font-medium">E2E Encrypted</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 py-6">
        <Tabs defaultValue="browse" className="w-full">
          <div className="flex items-center justify-between mb-4">
            <TabsList className="bg-white border border-zinc-200 rounded-full p-1">
              <TabsTrigger value="browse" className="rounded-full data-[state=active]:bg-zinc-900 data-[state=active]:text-white text-sm">Browse</TabsTrigger>
              <TabsTrigger value="my-rooms" className="rounded-full data-[state=active]:bg-zinc-900 data-[state=active]:text-white text-sm">My Rooms</TabsTrigger>
            </TabsList>
            <div className="flex items-center gap-2">
              <button onClick={handleAdultToggle}
                className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-colors ${showAdult ? 'bg-rose-50 border-rose-300 text-rose-600' : 'bg-zinc-50 border-zinc-200 text-zinc-500'}`}
                data-testid="adult-toggle">
                <Lock size={10} className="inline mr-1" />{showAdult ? '18+ ON' : '18+'}
              </button>
              <Dialog>
                <DialogTrigger asChild>
                  <Button className="bg-[#b8ff00] text-black rounded-full font-semibold text-sm" data-testid="create-room-btn">
                    <Plus size={16} className="mr-1" /> Create Room
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white max-w-md">
                  <DialogHeader><DialogTitle>Create Creator Room</DialogTitle></DialogHeader>
                  <div className="space-y-3 pt-2">
                    <Input placeholder="Room name" value={newRoom.name} onChange={e => setNewRoom({ ...newRoom, name: e.target.value })} data-testid="room-name-input" />
                    <Textarea placeholder="Description" value={newRoom.description} onChange={e => setNewRoom({ ...newRoom, description: e.target.value })} rows={2} />
                    <Input placeholder="Free preview text (shown to non-subscribers)" value={newRoom.preview_text} onChange={e => setNewRoom({ ...newRoom, preview_text: e.target.value })} />
                    <div className="grid grid-cols-2 gap-3">
                      <Select value={newRoom.category} onValueChange={v => setNewRoom({ ...newRoom, category: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                      <div className="relative">
                        <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                        <Input type="number" min={10} value={newRoom.price_per_month} onChange={e => setNewRoom({ ...newRoom, price_per_month: parseFloat(e.target.value) || 10 })}
                          className="pl-8" data-testid="price-input" />
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={newRoom.is_broadcast} onChange={e => setNewRoom({ ...newRoom, is_broadcast: e.target.checked })} className="rounded" />
                        <Video size={14} /> Broadcast (1-to-many)
                      </label>
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={newRoom.is_adult} onChange={e => setNewRoom({ ...newRoom, is_adult: e.target.checked })} className="rounded" />
                        <Lock size={14} /> Adult (18+)
                      </label>
                    </div>
                    <p className="text-xs text-zinc-500">Revenue split: You keep 80%, MMarketing 20%. Tips: 90/10. Min: $10/mo</p>
                    <Button onClick={handleCreate} className="w-full bg-gradient-to-r from-rose-500 to-pink-600 text-white rounded-xl font-bold" data-testid="submit-create-btn">Create Room</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Browse Tab */}
          <TabsContent value="browse">
            {rooms.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-zinc-200">
                <Radio className="mx-auto mb-4 text-zinc-300" size={48} />
                <h3 className="font-display text-xl text-zinc-900 mb-2">No Rooms Yet</h3>
                <p className="text-zinc-500 text-sm">Be the first to create a room!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {rooms.map((room, idx) => (
                  <motion.div key={room.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}>
                    <Link to={`/creator/${room.id}`}>
                      <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden hover:border-zinc-300 transition-all hover:shadow-lg group" data-testid={`creator-room-${room.id}`}>
                        <div className={`h-32 relative ${room.is_adult ? 'bg-gradient-to-br from-rose-900 to-pink-900' : 'bg-gradient-to-br from-violet-500 to-cyan-500'}`}>
                          {room.is_live && (
                            <div className="absolute top-3 left-3 flex items-center gap-1 px-2 py-1 bg-red-500 rounded-full">
                              <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                              <span className="text-xs text-white font-bold">LIVE</span>
                            </div>
                          )}
                          {room.is_adult && <Lock className="absolute top-3 right-3 text-white/40" size={20} />}
                          <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                            <div className="flex items-center gap-1 text-white/80 text-xs">
                              <Users size={12} /><span>{room.subscriber_count} subs</span>
                            </div>
                            <span className="text-xs bg-white/20 backdrop-blur-sm text-white px-2 py-0.5 rounded-full">{room.is_broadcast ? 'Broadcast' : '1-on-1'}</span>
                          </div>
                        </div>
                        <div className="p-4">
                          <h3 className="font-semibold text-zinc-900 truncate">{room.name}</h3>
                          <p className="text-xs text-zinc-500 mt-0.5">by {room.creator_name} &middot; {room.category}</p>
                          {room.preview_text && <p className="text-xs text-zinc-400 mt-2 line-clamp-2">{room.preview_text}</p>}
                          <div className="flex items-center justify-between mt-3">
                            <span className="font-display font-bold text-lg text-zinc-900">{formatCurrency(room.price_per_month)}<span className="text-xs text-zinc-400 font-normal">/mo</span></span>
                            <Button size="sm" className="bg-[#b8ff00] text-black rounded-full text-xs font-bold group-hover:scale-105 transition-transform">Subscribe</Button>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* My Rooms Tab */}
          <TabsContent value="my-rooms">
            {myRooms.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-zinc-200">
                <Crown className="mx-auto mb-4 text-zinc-300" size={48} />
                <h3 className="font-display text-xl text-zinc-900 mb-2">No Rooms Created</h3>
                <p className="text-zinc-500 text-sm">Create your first room and start earning!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {myRooms.map(room => (
                  <Link key={room.id} to={`/creator/${room.id}`}>
                    <div className="bg-white border border-zinc-200 rounded-2xl p-4 hover:border-zinc-300 transition-colors flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${room.is_live ? 'bg-red-100' : 'bg-violet-100'}`}>
                          {room.is_live ? <Zap className="text-red-500" size={20} /> : <Radio className="text-violet-500" size={20} />}
                        </div>
                        <div>
                          <h3 className="font-semibold text-zinc-900">{room.name}</h3>
                          <p className="text-xs text-zinc-500">{room.subscriber_count} subscribers &middot; {formatCurrency(room.price_per_month)}/mo</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-mono font-bold text-green-600">{formatCurrency(room.total_tips || 0)} tips</p>
                        {room.is_live && <span className="text-xs text-red-500 font-bold">LIVE NOW</span>}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
